import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { motion } from "framer-motion";
import { Mic, MessageCircle, Video } from "lucide-react";

const Hero = () => {
  return (
    <section className="relative overflow-hidden py-20 md:py-32">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br -z-10" />
      <div className="absolute top-0 left-0 w-full h-full opacity-70 -z-10" />
      
      {/* Abstract shapes */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-primary/30 rounded-full blur-3xl -z-10" />
      
      <div className="container px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <motion.h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-gray-900"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                A therapy platform for the <span className="text-primary">BIPOC community</span>
              </motion.h1>
              <motion.p 
                className="text-xl text-gray-600 max-w-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                We match you with handpicked, culturally competent therapists who understand your unique experience.
              </motion.p>
            </div>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <a href="/assessments">
                <Button variant="secondary" className="rounded-full text-white px-8 py-6 h-auto text-lg">
                  Take the Mental Health Quiz
                </Button>
              </a>
              <a href="/intake_request_without_quiz/new">
                <Button variant="outline" className="rounded-full px-8 py-6 h-auto text-lg">
                  Match Me With A Therapist
                </Button>
              </a>
            </motion.div>
          </div>
          <motion.div
            className="relative hidden md:block"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <div className="relative aspect-square overflow-hidden rounded-2xl  shadow-xl">
              {/* Video Call Interface Mockup */}
              <div className="absolute inset-0 bg-primary/50 p-4">
                {/* Header with Logo and Video Call Indicator */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="bg-primary text-white rounded-md px-2 py-1">
                      <span>Mind</span><span className="font-bold">Pulse</span>
                    </div>
                    <div className="bg-green-500 px-2 py-0.5 rounded text-xs text-white flex items-center">
                      <Video size={12} className="mr-1" />
                      Live Session
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="bg-primary-200 p-1 rounded-full">
                      <Mic size={16} className="text-primary-700" />
                    </div>
                    <div className="bg-primary-200 p-1 rounded-full">
                      <MessageCircle size={16} className="text-primary-700" />
                    </div>
                  </div>
                </div>
                
                {/* Video Grid with "Connected" indicators */}
                <div className="grid grid-cols-2 gap-3 h-[70%]">
                  {/* Therapist Video */}
                  <div className="relative rounded-lg overflow-hidden bg-purple-200 border-2 border-green-500">
                    <img
                      src="https://res.cloudinary.com/dm9jepekh/image/upload/v1745971519/50291_fjr3kc.jpg"
                      alt="Therapist"
                      className="object-cover w-full h-full"
                    />
                    <div className="absolute top-2 right-2 bg-red-700 rounded-full w-3 h-3"></div>
                    <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded flex items-center">
                      <Mic size={10} className="mr-1" />
                      Priya Sharma (Therapist)
                    </div>
                  </div>
                  
                  {/* Patient Video - Updated with South Asian female looking at camera */}
                  <div className="relative rounded-lg overflow-hidden bg-purple-200 border-2 border-green-500">
                    <img
                      src="https://res.cloudinary.com/dm9jepekh/image/upload/v1745971271/2148860585_texbjf.jpg"
                      alt="Patient"
                      className="object-cover w-full h-full"
                    />
                    <div className="absolute top-2 right-2 bg-red-700 rounded-full w-3 h-3"></div>
                    <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded flex items-center">
                      <Mic size={10} className="mr-1" />
                      Meera Patel
                    </div>
                  </div>
                </div>
                
                {/* Transcript Section */}
                <div className="mt-4 bg-white rounded-lg p-3 h-[20%] overflow-hidden border border-primary-200">
                  <div className="text-xs text-gray-500 font-bold mb-1">Auto-Transcript</div>
                  <div className="text-gray-800 text-sm space-y-2 overflow-y-auto h-[85%]">
                    <p><span className="font-bold">Dr. Sharma:</span> How have you been managing those anxiety symptoms we discussed last week?</p>
                    <p><span className="font-bold">Meera Patel:</span> I've been trying the breathing exercises you suggested, and they've helped during stressful moments at work.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
